<template>
  <Navigation :title="$t('uploadFinger.title')" />

  <!-- Tutorial wizard -->
  <div v-show="!isTutorialWatched">
    <div class="tutorial-hero bg-finger">
      <div class="hero-spacer"></div>
    </div>

    <div class="tutorial-body">
      <div class="tutorial-card card-priadi">
        <p class="tutorial-card-title">{{ $t('uploadFinger.tutorialTitle') }}</p>
        <p class="tutorial-card-subtitle">{{ $t('uploadFinger.tutorialSubtitle') }}</p>

        <div class="wizard-viewport">
          <img :src="tutorialImages[currentStep]" class="wizard-img" :alt="`Step ${currentStep + 1}`" />
        </div>

        <div class="wizard-dots">
          <span
            v-for="(_, index) in tutorialImages"
            :key="index"
            class="wizard-dot"
            :class="{ active: index === currentStep }">
          </span>
        </div>

        <div class="wizard-nav">
          <button
            v-if="currentStep > 0"
            class="btn-priadi-outline wizard-back"
            @click="currentStep--">
            <i class="bi bi-arrow-left"></i> {{ $t('confirmation.back') }}
          </button>
          <button
            v-if="currentStep < tutorialImages.length - 1"
            class="btn-priadi wizard-next"
            @click="currentStep++">
            {{ $t('confirmation.next') }} <i class="bi bi-arrow-right"></i>
          </button>
          <button
            v-else
            class="btn-priadi btn-priadi-block wizard-finish"
            @click="isTutorialWatched = true">
            {{ $t('continue') }} <i class="bi bi-arrow-right"></i>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Capture phase -->
  <Transition name="fade">
    <div v-show="isTutorialWatched">
      <div class="capture-hero bg-finger">
        <div class="hero-spacer"></div>
      </div>

      <div class="capture-body">
        <!-- Progress -->
        <div class="progress-card card-priadi">
          <div class="progress-text">
            <i class="bi bi-fingerprint progress-icon"></i>
            <span>{{ $t('uploadFinger.progress', { count: capturedCount }) }}</span>
          </div>
          <div class="progress-bar-track">
            <div class="progress-bar-fill" :style="{ width: (capturedCount / 10 * 100) + '%' }"></div>
          </div>
          <p class="progress-hint" v-if="capturedCount < 10">{{ $t('uploadFinger.tapToRetake') }}</p>
        </div>

        <!-- Left hand -->
        <section class="hand-section">
          <p class="hand-label">{{ $t('uploadFinger.leftHand') }}</p>
          <div class="finger-row">
            <div
              v-for="finger in hands[0].fingers"
              :key="finger.name"
              class="finger-slot"
              :class="{
                'finger-done': fingers_done[finger.name],
                'finger-selected': finger.name === selected_finger
              }"
              @click="changeFinger(finger.name)">
              <div class="finger-thumb-wrap">
                <img
                  :id="`img${finger.name}`"
                  :src="fingers_done[finger.name]
                    ? `https://api-apps.priadi.id/${fingers_image[finger.name]}?v=${fingers_version[finger.name]}`
                    : `/img/finger/${finger.name}.jpg`"
                  class="finger-thumb-img"
                  :alt="fingerName(finger.name)" />
                <span class="finger-check" v-if="fingers_done[finger.name]">
                  <i class="bi bi-check-lg"></i>
                </span>
              </div>
              <span class="finger-label">{{ fingerName(finger.name) }}</span>
              <input
                type="file"
                class="d-none"
                :id="`cameraFileInput${finger.name}`"
                accept="image/*"
                capture="environment"
                @change="onFileChange" />
            </div>
          </div>
        </section>

        <!-- Right hand -->
        <section class="hand-section">
          <p class="hand-label">{{ $t('uploadFinger.rightHand') }}</p>
          <div class="finger-row">
            <div
              v-for="finger in hands[1].fingers"
              :key="finger.name"
              class="finger-slot"
              :class="{
                'finger-done': fingers_done[finger.name],
                'finger-selected': finger.name === selected_finger
              }"
              @click="changeFinger(finger.name)">
              <div class="finger-thumb-wrap">
                <img
                  :id="`img${finger.name}`"
                  :src="fingers_done[finger.name]
                    ? `https://api-apps.priadi.id/${fingers_image[finger.name]}?v=${fingers_version[finger.name]}`
                    : `/img/finger/${finger.name}.jpg`"
                  class="finger-thumb-img"
                  :alt="fingerName(finger.name)" />
                <span class="finger-check" v-if="fingers_done[finger.name]">
                  <i class="bi bi-check-lg"></i>
                </span>
              </div>
              <span class="finger-label">{{ fingerName(finger.name) }}</span>
              <input
                type="file"
                class="d-none"
                :id="`cameraFileInput${finger.name}`"
                accept="image/*"
                capture="environment"
                @change="onFileChange" />
            </div>
          </div>
        </section>

        <!-- Finish button (sticky bottom) -->
        <div class="finish-bar" v-if="capturedCount === 10">
          <button class="btn-priadi btn-priadi-block finish-btn" @click="finish">
            <i class="bi bi-check-circle"></i> {{ $t('uploadFinger.finish') }}
          </button>
        </div>
      </div>

      <canvas id="photoTaken" class="d-none" ref="canvas" :width="360" :height="480"></canvas>
    </div>
  </Transition>

  <!-- Retake confirmation modal -->
  <div
    class="modal fade"
    :class="{ show: showRetakeModal }"
    :style="{ display: showRetakeModal ? 'block' : 'none' }"
    tabindex="-1"
    role="dialog"
    aria-modal="true"
    aria-labelledby="retakeModalTitle">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content retake-modal">
        <div class="retake-modal-header">
          <h2 class="retake-modal-title" id="retakeModalTitle">{{ $t('uploadFinger.retakeTitle') }}</h2>
        </div>
        <div class="retake-modal-body">
          <p class="retake-modal-text">{{ $t('uploadFinger.retake') }}</p>
          <div class="retake-modal-actions">
            <button class="btn-priadi-outline retake-action-cancel" @click="showRetakeModal = false">
              {{ $t('uploadFinger.retakeCancel') }}
            </button>
            <button class="btn-priadi retake-action-confirm" @click="retakeProcess">
              {{ $t('uploadFinger.retakeConfirm') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div
    class="modal-backdrop fade"
    :class="{ show: showRetakeModal }"
    :style="{ display: showRetakeModal ? 'block' : 'none' }">
  </div>
</template>

<script>
import Navigation from '../components/Navigation'
import Swal from 'sweetalert2'
import axios from 'axios'

export default {
  name: 'CaptureStamp',
  components: { Navigation },
  data() {
    return {
      transaction_id: '',
      transaction_data: {},
      hands: [
        {
          name: 'leftHand',
          fingers: [
            { code: 10, name: 'left_thumb' },
            { code: 11, name: 'left_forefinger' },
            { code: 12, name: 'left_middlefinger' },
            { code: 13, name: 'left_thirdfinger' },
            { code: 14, name: 'left_littlefinger' },
          ],
        },
        {
          name: 'rightHand',
          fingers: [
            { code: 21, name: 'right_thumb' },
            { code: 22, name: 'right_forefinger' },
            { code: 23, name: 'right_middlefinger' },
            { code: 24, name: 'right_thirdfinger' },
            { code: 25, name: 'right_littlefinger' },
          ],
        },
      ],
      fingers_done: {
        left_thumb: false,
        left_forefinger: false,
        left_middlefinger: false,
        left_thirdfinger: false,
        left_littlefinger: false,
        right_thumb: false,
        right_forefinger: false,
        right_middlefinger: false,
        right_thirdfinger: false,
        right_littlefinger: false,
      },
      fingers_image: {
        left_thumb: '',
        left_forefinger: '',
        left_middlefinger: '',
        left_thirdfinger: '',
        left_littlefinger: '',
        right_thumb: '',
        right_forefinger: '',
        right_middlefinger: '',
        right_thirdfinger: '',
        right_littlefinger: '',
      },
      fingers_version: {
        left_thumb: '',
        left_forefinger: '',
        left_middlefinger: '',
        left_thirdfinger: '',
        left_littlefinger: '',
        right_thumb: '',
        right_forefinger: '',
        right_middlefinger: '',
        right_thirdfinger: '',
        right_littlefinger: '',
      },
      selected_finger: '',
      isTutorialWatched: false,
      showRetakeModal: false,
      currentStep: 0,
    }
  },
  computed: {
    locale() {
      return this.$i18n.locale
    },
    tutorialImages() {
      const locale = this.$i18n.locale === 'id' ? 'id' : 'en'
      return [
        `/img/tatacara1-${locale}.webp`,
        `/img/tatacara2-${locale}.webp`,
      ]
    },
    capturedCount() {
      return Object.values(this.fingers_done).filter(Boolean).length
    },
  },
  mounted() {
    document.body.classList.add('body-tinted')
    this.isTutorialWatched = false
    this.transaction_id = this.$store.getters.getTransaction
    this.checkFinger()
    this.getTransactionData()
  },
  unmounted() {
    document.body.classList.remove('body-tinted')
  },
  methods: {
    fingerName(name) {
      const names = {
        left_thumb: 'Thumb',
        left_forefinger: 'Index',
        left_middlefinger: 'Middle',
        left_thirdfinger: 'Ring',
        left_littlefinger: 'Little',
        right_thumb: 'Thumb',
        right_forefinger: 'Index',
        right_middlefinger: 'Middle',
        right_thirdfinger: 'Ring',
        right_littlefinger: 'Little',
      }
      return names[name] || name
    },
    changeFinger(finger_name) {
      this.selected_finger = finger_name
      if (this.fingers_done[finger_name]) {
        this.showRetakeModal = true
      } else {
        this.takingFinger()
      }
    },
    takingFinger() {
      document.getElementById('cameraFileInput' + this.selected_finger).click()
    },
    retakeProcess() {
      this.showRetakeModal = false
      this.takingFinger()
    },
    onFileChange(event) {
      const self = this
      const file = event.target.files[0]
      if (file) {
        const fileReader = new FileReader()
        fileReader.onload = function (e) {
          const img = new Image()
          img.onload = function () {
            const canvas = document.getElementById('photoTaken')
            const ctx = canvas.getContext('2d')
            const MAX_WIDTH = 800
            const MAX_HEIGHT = 600
            let width = img.width
            let height = img.height
            if (width > height) {
              if (width > MAX_WIDTH) {
                height *= MAX_WIDTH / width
                width = MAX_WIDTH
              }
            } else {
              if (height > MAX_HEIGHT) {
                width *= MAX_HEIGHT / height
                height = MAX_HEIGHT
              }
            }
            canvas.width = width
            canvas.height = height
            ctx.drawImage(img, 0, 0, width, height)
            self.sendPicture()
          }
          img.src = e.target.result
        }
        fileReader.readAsDataURL(file)
      }
    },
    async sendPicture() {
      let self = this
      let finger_image = document.getElementById('photoTaken').toDataURL('image/jpg', 1.0)
      let trans_id = this.transaction_id
      let finger_name = this.selected_finger

      let form = new FormData()
      form.append('id', trans_id)
      form.append('fingername', finger_name)
      form.append('image', finger_image)

      await axios
        .post('/api/update_finger_stamp', form)
        .then(function (response) {
          // console.log(response.data)

          let swal_icon = response.data.status ? 'success' : 'error'
          let swal_text = response.data.status ? self.$t('saved') : self.$t('failed')
          Swal.fire({
            icon: swal_icon,
            title: swal_text,
            showConfirmButton: false,
            timer: 1500,
          })
          if (response.data.status) {
            self.fingers_done[self.selected_finger] = true
          }
          self.showRetakeModal = false
          self.fingers_version[self.selected_finger] = Date.now()
          self.selected_finger = ''
          document.getElementById('img' + finger_name).src = finger_image
          self.checkFinger()
        })
        .catch(function (error) {
          console.log("hei "+error)
          Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to upload fingerprint. Please try again.' })
        })
    },
    finish() {
      let self = this
      let send_url
      let send_data
      if (this.$store.getters.getRole === 'hr' || self.transaction_data.status_process == 'retake') {
        send_url = '/api/send_person_priadi'
        send_data = { transaction_id: self.transaction_id }
      } else {
        send_url = '/api/set_done'
        send_data = { id: self.transaction_id }
      }
      axios
        .post(send_url, send_data)
        .then(function () {
          if (self.$store.getters.getRole === 'hr' || self.transaction_data.status_process == 'retake') {
            Swal.fire({
              icon: 'success',
              title: self.$t('success'),
              confirmButtonText: self.$t('uploadFinger.continueTransaction'),
            }).then((result) => {
              if (result.isConfirmed) {
                self.$router.push('/transactions')
              }
            })
          } else {
            Swal.fire({
              icon: 'success',
              title: self.$t('success'),
              showDenyButton: true,
              showCancelButton: true,
              confirmButtonText: self.$t('uploadFinger.continuePayment'),
              denyButtonText: self.$t('uploadFinger.continueTransaction'),
              cancelButtonText: self.$t('uploadFinger.continueTakeAnother'),
              allowOutsideClick: false,
              allowEscapeKey: false,
              allowEnterKey: false,
            }).then((result) => {
              if (result.isConfirmed) {
                let formData = {}
                axios
                  .post('/api/create_invoice_pg', {
                    user_id: self.$store.getters.getUser.id,
                    transaction_id: self.transaction_id,
                    package_name: self.transaction_data.package_name,
                    price: self.transaction_data.total_price,
                  })
                  .then((response) => {
                    formData.payment_id = response.data.id
                    formData.payment_url = response.data.url
                    formData.trx_id = self.transaction_id
                    formData.price_sum = self.transaction_data.total_price
                    formData.package_name = self.transaction_data.package_name
                    self.$store.dispatch('setCheckout', formData)
                    self.$router.push('/payment/checkout')
                  })
              } else if (result.isDenied) {
                self.$router.push('/transactions')
              } else if (result.isDismissed) {
                self.$router.push('/package')
              }
            })
          }
        })
        .catch(function (error) {
          console.log(error)
          Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to submit fingerprints. Please try again.' })
        })
    },
    async checkFinger() {
      let self = this
      await axios
        .post('/api/check_finger', { id: this.transaction_id })
        .then(function (response) {
          let response_data = response.data
          Object.keys(response_data).forEach(function (key) {
            if (response_data[key] !== null) {
              self.fingers_done[key] = true
              self.fingers_image[key] = response_data[key]
              self.fingers_version[key] = Date.now()
            }
          })
        })
        .catch(function (error) {
          console.log(error)
          Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to check fingerprint status.' })
        })
    },
    async getTransactionData() {
      let self = this
      await axios
        .post('/api/check_transaction', { id: this.transaction_id })
        .then(function (response) {
          self.transaction_data = response.data
        })
        .catch(function (error) {
          console.log(error)
          Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load transaction data.' }).then(() => {
            self.$router.push('/transactions')
          })
        })
    },
  },
}
</script>

<style scoped>
/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Tutorial */
.tutorial-hero {
  position: relative;
}

.tutorial-body {
  padding: 0 var(--space-4) var(--space-8);
}

.tutorial-card {
  padding: var(--space-6);
  margin-top: -4em;
  position: relative;
  z-index: 1;
}

.tutorial-card-title {
  font-size: var(--text-lg);
  font-weight: var(--font-bold);
  color: var(--color-text-primary);
  text-align: center;
  margin-bottom: var(--space-1);
}

.tutorial-card-subtitle {
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
  text-align: center;
  margin-bottom: var(--space-6);
}

/* Wizard viewport */
.wizard-viewport {
  width: 100%;
  margin-bottom: var(--space-4);
}

.wizard-img {
  width: 100%;
  border-radius: var(--radius-md);
}

/* Wizard dots */
.wizard-dots {
  display: flex;
  justify-content: center;
  gap: var(--space-2);
  margin-bottom: var(--space-6);
}

.wizard-dot {
  width: 8px;
  height: 8px;
  border-radius: var(--radius-full);
  background-color: var(--color-border);
  transition: background-color var(--transition-base), width var(--transition-base);
}

.wizard-dot.active {
  width: 24px;
  background-color: var(--color-primary);
}

/* Wizard nav */
.wizard-nav {
  display: flex;
  gap: var(--space-3);
}

.wizard-back {
  flex: 0 0 auto;
  border-radius: var(--radius-full);
  padding: var(--space-3) var(--space-5);
  font-size: var(--text-base);
  white-space: nowrap;
}

.wizard-next {
  flex: 1;
  border-radius: var(--radius-full);
  font-weight: var(--font-semibold);
}

.wizard-finish {
  border-radius: var(--radius-full);
  font-weight: var(--font-semibold);
}

/* Capture */
.capture-hero {
  position: relative;
}

.capture-body {
  padding: 0 var(--space-4) calc(var(--space-16) + var(--space-10));
}

/* Progress card */
.progress-card {
  padding: var(--space-4);
  margin-top: -4em;
  position: relative;
  z-index: 1;
  margin-bottom: var(--space-6);
}

.progress-text {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--text-base);
  font-weight: var(--font-semibold);
  color: var(--color-text-primary);
  margin-bottom: var(--space-2);
}

.progress-icon {
  color: var(--color-primary);
  font-size: var(--text-md);
}

.progress-bar-track {
  height: 8px;
  background-color: var(--color-background);
  border-radius: var(--radius-full);
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background-color: var(--color-primary);
  border-radius: var(--radius-full);
  transition: width 0.4s ease;
}

.progress-hint {
  font-size: var(--text-xs);
  color: var(--color-text-muted);
  margin-top: var(--space-2);
  margin-bottom: 0;
}

/* Hand sections */
.hand-section {
  margin-bottom: var(--space-6);
}

.hand-label {
  font-size: var(--text-sm);
  font-weight: var(--font-semibold);
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: var(--space-3);
}

/* Finger row — 5 columns */
.finger-row {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: var(--space-2);
}

/* Finger slot */
.finger-slot {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-1);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.finger-thumb-wrap {
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  border-radius: var(--radius-md);
  border: 2px solid var(--color-border);
  overflow: hidden;
  transition: border-color var(--transition-base), box-shadow var(--transition-base);
}

.finger-slot:active .finger-thumb-wrap {
  transform: scale(0.95);
}

.finger-done .finger-thumb-wrap {
  border-color: var(--color-success);
}

.finger-selected .finger-thumb-wrap {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(var(--color-primary-rgb), 0.2);
}

.finger-thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.finger-check {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  border-radius: var(--radius-full);
  background-color: var(--color-success);
  color: var(--color-text-inverse);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: var(--font-bold);
  line-height: 1;
}

.finger-label {
  font-size: var(--text-xs);
  color: var(--color-text-secondary);
  text-align: center;
  line-height: var(--leading-tight);
}

/* Finish button — sticky bottom */
.finish-bar {
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: var(--container-max);
  padding: var(--space-4);
  background-color: var(--color-surface);
  border-top: 1px solid var(--color-border);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
  z-index: var(--z-nav);
}

.finish-btn {
  border-radius: var(--radius-full);
  font-weight: var(--font-semibold);
  background-color: var(--color-success);
}

.finish-btn:hover {
  background-color: #059669;
}

/* Retake modal */
.retake-modal {
  border-radius: var(--radius-lg);
  border: none;
  box-shadow: var(--shadow-lg);
  margin: var(--space-4);
  overflow: hidden;
}

.retake-modal-header {
  background: linear-gradient(
    160deg,
    rgba(var(--color-primary-rgb), 0.08) 0%,
    rgba(var(--color-primary-rgb), 0.02) 100%
  );
  padding: var(--space-6) var(--space-6) var(--space-4);
  text-align: center;
}

.retake-modal-title {
  font-size: var(--text-lg);
  font-weight: var(--font-bold);
  color: var(--color-text-primary);
  margin: 0;
}

.retake-modal-body {
  padding: var(--space-4) var(--space-6) var(--space-6);
}

.retake-modal-text {
  font-size: var(--text-base);
  color: var(--color-text-secondary);
  text-align: center;
  margin-bottom: var(--space-6);
}

.retake-modal-actions {
  display: flex;
  gap: var(--space-3);
}

.retake-action-cancel {
  flex: 1;
  border-radius: var(--radius-full);
  padding: var(--space-3) var(--space-4);
  font-size: var(--text-sm);
}

.retake-action-confirm {
  flex: 1;
  border-radius: var(--radius-full);
  padding: var(--space-3) var(--space-4);
  font-size: var(--text-sm);
  font-weight: var(--font-semibold);
}
</style>
